import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    buildDistortionGridSvg,
    type DistortionAlgorithm,
} from "../../utils/distortionGrid";
import {
    parseViewBoxParts,
    viewBoxAttrFromSvg,
} from "../../utils/channelSplitVector";
import { useSvgToolHandoffReceiver } from "../../hooks/useSvgToolHandoffReceiver";
import { isValidSvg } from "../../utils/svgUtils";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";

interface UploadedSvg {
    name: string;
    content: string;
}

interface DistortionGridState {
    svg: UploadedSvg | null;
    error: string | null;
    svgInputRef: React.RefObject<HTMLInputElement>;
    handleSvgLoad: (e: React.ChangeEvent<HTMLInputElement>) => void;

    cols: number;
    rows: number;
    maxStrength: number;
    algorithm: DistortionAlgorithm;
    seed: string;
    noiseScale: number;
    octaves: number;
    circleSegments: number;

    setCols: (v: number) => void;
    setRows: (v: number) => void;
    setMaxStrength: (v: number) => void;
    setAlgorithm: (v: DistortionAlgorithm) => void;
    setSeed: (v: string) => void;
    setNoiseScale: (v: number) => void;
    setOctaves: (v: number) => void;
    setCircleSegments: (v: number) => void;

    viewBoxAttr: string;
    vb: { x: number; y: number; width: number; height: number };
    exportSvgString: string | null;
    canExport: boolean;

    handleDownload: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
}

const DistortionGridContext = createContext<DistortionGridState | null>(null);

const DEFAULT_DISTORTION_GRID_PRESET: Record<string, unknown> = {
    cols: 4,
    rows: 4,
    maxStrength: 40,
    algorithm: "fbm",
    seed: "grid",
    noiseScale: 0.045,
    octaves: 2,
    circleSegments: 48,
};

export function useDistortionGrid() {
    const ctx = useContext(DistortionGridContext);
    if (!ctx) throw new Error("useDistortionGrid must be used within DistortionGridProvider");
    return ctx;
}

export function DistortionGridProvider({ children }: { children: React.ReactNode }) {
    const [svg, setSvg] = useState<UploadedSvg | null>(null);
    const [error, setError] = useState<string | null>(null);
    const svgInputRef = useRef<HTMLInputElement | null>(null);

    const [cols, setCols] = useState(4);
    const [rows, setRows] = useState(4);
    const [maxStrength, setMaxStrength] = useState(40);
    const [algorithm, setAlgorithm] = useState<DistortionAlgorithm>("fbm");
    const [seed, setSeed] = useState("grid");
    const [noiseScale, setNoiseScale] = useState(0.045);
    const [octaves, setOctaves] = useState(2);
    const [circleSegments, setCircleSegments] = useState(48);

    const handleSvgLoad = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setError(null);

        if (!file.type.includes("svg") && !file.name.toLowerCase().endsWith(".svg")) {
            setError("Please choose an SVG file.");
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const text = typeof reader.result === "string" ? reader.result : "";
            if (!text) { setError("Unable to read SVG file."); return; }
            if (!isValidSvg(text)) { setError("File does not appear to be valid SVG."); return; }
            setSvg({ name: file.name, content: text });
        };
        reader.onerror = () => setError("Error reading file.");
        reader.readAsText(file);
    }, []);

    const viewBoxAttr = useMemo(
        () => (svg ? viewBoxAttrFromSvg(svg.content) : "0 0 100 100"),
        [svg]
    );

    const vb = useMemo(() => {
        const p = parseViewBoxParts(viewBoxAttr);
        return p ?? { x: 0, y: 0, width: 100, height: 100 };
    }, [viewBoxAttr]);

    const exportSvgString = useMemo(() => {
        if (!svg) return null;
        return buildDistortionGridSvg({
            svgContent: svg.content,
            cols,
            rows,
            maxStrength,
            algorithm,
            seed,
            noiseScale,
            octaves,
            circleSegments,
        });
    }, [svg, cols, rows, maxStrength, algorithm, seed, noiseScale, octaves, circleSegments]);

    const canExport = !!exportSvgString;

    const handleDownload = useCallback(() => {
        if (!exportSvgString || !svg) return;
        const blob = new Blob([exportSvgString], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const base = svg.name.replace(/\.svg$/i, "");
        link.download = `${base}-distortion-grid.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [exportSvgString, svg]);

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            cols, rows, maxStrength, algorithm, seed, noiseScale, octaves, circleSegments,
        }),
        [cols, rows, maxStrength, algorithm, seed, noiseScale, octaves, circleSegments]
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.cols != null) setCols(values.cols as number);
        if (values.rows != null) setRows(values.rows as number);
        if (values.maxStrength != null) setMaxStrength(values.maxStrength as number);
        if (values.algorithm != null) {
            const a = values.algorithm as string;
            setAlgorithm((a === "noise" ? "fbm" : a) as DistortionAlgorithm);
        }
        if (values.seed != null) setSeed(values.seed as string);
        if (values.noiseScale != null) setNoiseScale(values.noiseScale as number);
        if (values.octaves != null) setOctaves(values.octaves as number);
        if (values.circleSegments != null) setCircleSegments(values.circleSegments as number);
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "distortion-grid",
        defaultPreset: DEFAULT_DISTORTION_GRID_PRESET,
        getPresetValues,
        applyPreset,
    });

    useSvgToolHandoffReceiver(
        "distortion-grid",
        {
            onPrimary: (svg) => {
                setError(null);
                setSvg(svg);
            },
        },
        { setError },
    );

    const value: DistortionGridState = {
        svg,
        error,
        svgInputRef,
        handleSvgLoad,
        cols,
        rows,
        maxStrength,
        algorithm,
        seed,
        noiseScale,
        octaves,
        circleSegments,
        setCols,
        setRows,
        setMaxStrength,
        setAlgorithm,
        setSeed,
        setNoiseScale,
        setOctaves,
        setCircleSegments,
        viewBoxAttr,
        vb,
        exportSvgString,
        canExport,
        handleDownload,
        applyPreset,
        getPresetValues,
        resetParameters,
    };

    return (
        <DistortionGridContext.Provider value={value}>{children}</DistortionGridContext.Provider>
    );
}
